// filepath: apps/web/src/auth/bootstrapErrorStates.tsx
//
// FR-UI-CORE-2 bootstrap error screens (AC-5).
//
// Five screens, each maps to a specific bootstrap failure mode.
// These are platform chrome (not tenant-authored definitions) — they
// render the same for every tenant. No tenant-specific content is
// fetched; the messages are neutral and never reveal internal
// identifiers.
//
// Why dedicated screens: the previous scaffold rendered a generic
// "Error 500" for every bootstrap failure. ARCHITECTURE_WEB.md §3
// requires a dedicated screen per state so the user can act
// (retry, contact admin, etc.) and the operator can distinguish
// failure modes from the Sentry telemetry.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";

// Common shell — every screen wraps its content in this so the
// styling is consistent and the screen is greppable for the
// static-guard test (no dangerouslySetInnerHTML here).
function BootstrapErrorScreen({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-testid="bootstrap-error-screen"
      className="bootstrap-error"
      role="alert"
    >
      {children}
    </div>
  );
}

/**
 * TenantNotFoundScreen — rendered when GET /api/tenant-config returns 404.
 *
 * Cause: the subdomain does not map to a real tenant (was deleted,
 * was never provisioned, or the user mistyped the URL).
 *
 * Action: contact the administrator. There is no self-recovery; the
 * tenant must be provisioned via the saga.
 */
export function TenantNotFoundScreen(): React.ReactElement {
  return (
    <main aria-labelledby="tenant-not-found-title">
      <BootstrapErrorScreen>
        <h2 id="tenant-not-found-title">This workspace does not exist</h2>
        <p>
          The address you used isn&apos;t associated with a workspace. Check the
          URL, or contact your administrator.
        </p>
      </BootstrapErrorScreen>
    </main>
  );
}

/**
 * TenantSuspendedScreen — rendered when /api/tenant-config returns 503.
 *
 * Cause: the tenant is disabled (enabled=false in platform.tenants).
 * Either the operator suspended the tenant or the billing flow
 * disabled it.
 *
 * Action: contact the administrator. The retry button is provided
 * in case the suspension is lifted while the user is on the page.
 */
export function TenantSuspendedScreen(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <main aria-labelledby="tenant-suspended-title">
      <BootstrapErrorScreen>
        <h2 id="tenant-suspended-title">
          This workspace is temporarily unavailable
        </h2>
        <p>
          Your administrator has suspended this workspace. Please contact them
          for more information.
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          {t("common.retry")}
        </button>
      </BootstrapErrorScreen>
    </main>
  );
}

/**
 * NetworkFailureScreen — rendered when /api/tenant-config fails
 * with a network error (timeout, DNS failure, CORS preflight
 * rejection, etc.).
 *
 * The screen auto-retries with exponential backoff up to 3 times.
 * After 3 attempts, the user is shown a manual retry button.
 */
export interface NetworkFailureScreenProps {
  /**
   * Test seam — controls the retry attempts. In production, the
   * component owns the backoff schedule.
   */
  onRetry?: () => void | Promise<void>;
}

export function NetworkFailureScreen({
  onRetry,
}: NetworkFailureScreenProps = {}): React.ReactElement {
  const [attempt, setAttempt] = React.useState(0);

  // Trivial backoff: 1s, 2s, 4s. After 3 attempts, render the
  // manual retry button.
  React.useEffect(() => {
    if (onRetry === undefined) return;
    if (attempt >= 3) return;
    const delay = 2 ** attempt * 1000;
    const t = setTimeout(() => {
      setAttempt((n) => n + 1);
      void onRetry();
    }, delay);
    return () => clearTimeout(t);
  }, [attempt, onRetry]);

  const { t } = useTranslation();
  return (
    <main aria-labelledby="network-failure-title">
      <BootstrapErrorScreen>
        <h2 id="network-failure-title">Unable to connect</h2>
        <p>
          We can&apos;t reach the server. Check your network connection and try
          again.
        </p>
        {attempt >= 3 ? (
          <button type="button" onClick={() => setAttempt(0)}>
            {t("common.retry")}
          </button>
        ) : (
          <p>Retrying… (attempt {attempt + 1} of 3)</p>
        )}
      </BootstrapErrorScreen>
    </main>
  );
}

/**
 * OidcErrorScreen — rendered when the OIDC client surfaces an error
 * (auth.error from react-oidc-context). The error code is shown so
 * the user can quote it to support.
 *
 * Note: the error MESSAGE is shown (which is safe — it comes from
 * the OIDC library, not a user-supplied input). The error code is
 * also shown for support routing.
 */
export interface OidcErrorScreenProps {
  code?: string;
  message?: string;
}

export function OidcErrorScreen({
  code,
  message,
}: OidcErrorScreenProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <main aria-labelledby="oidc-error-title">
      <BootstrapErrorScreen>
        <h2 id="oidc-error-title">Sign-in failed</h2>
        <p>
          {message ?? "We couldn\u2019t complete sign-in. Please try again."}
        </p>
        {code ? (
          <p>
            Error code: <code>{code}</code>
          </p>
        ) : null}
        <button type="button" onClick={() => (window.location.href = "/")}>
          {t("common.back")}
        </button>
      </BootstrapErrorScreen>
    </main>
  );
}

/**
 * LoadingScreen — rendered while the bootstrap fetch is in flight.
 * Keeps the user in a known state so they don't see a blank page.
 */
export function LoadingScreen(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <main aria-labelledby="bootstrap-loading-title">
      <div
        data-testid="bootstrap-loading-screen"
        className="bootstrap-loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <h1 id="bootstrap-loading-title" className="visually-hidden">
          {t("common.loading")}
        </h1>
        <p>{t("common.loading")}…</p>
      </div>
    </main>
  );
}
